/**
 * Gelişmiş Fetal Baş 3D Viewer - Morph Tabanlı Profesyonel Deformasyon Sistemi
 * 
 * Bu bileşen, Blender Shape Keys / Blend Shapes mimarisini takip eden
 * profesyonel bir morph deformasyon sistemi kullanır.
 * 
 * Mimari:
 * 1. Temel fetal baş mesh'i
 * 2. Biyometrik girdilerin normalize edilmesi
 * 3. Biyometrik → Morph kanal eşlemesi (ağırlıklı)
 * 4. Düzeltici stabilizasyon katmanı
 * 5. Gerçek zamanlı önizleme
 * 
 * Morph Kanalları:
 * - head_width, head_length, head_height (kranial)
 * - forehead_width, forehead_projection (alın)
 * - nose_projection, nose_vertical (burun)
 * - eye_spacing, orbital_depth (göz)
 * - mouth_width, upper_lip_fullness (ağız)
 * - jaw_projection, chin_length (çene)
 * 
 * Türkçe Biyometrik Girdiler:
 * - BPD, HC, Önarka baş (OFD)
 * - Fromen, Burun, Göztepe, BiocÇap
 * - Çene, Ağızçapı, Göz, Üst Dudak
 */

import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { createFetalMorphSystem } from '../lib/fetal-morph';

/**
 * Referans biyometrik değerler (23-24. hafta ortalamaları)
 */
const REFERENCE = {
  bpd: 57.0,        // mm - Biparietal Diameter
  hc: 219.0,        // mm - Head Circumference
  ofd: 75.0,        // mm - Occipitofrontal Diameter
  fromen: 31.0,     // mm - Alın yüksekliği
  burun: 8.0,       // mm - Burun uzunluğu
  goztepe: 23.0,    // mm - Göz arası mesafe
  bioc_cap: 41.0,   // mm - Yüz genişliği
  cene: 20.0,       // mm - Çene uzunluğu
  agiz_capi: 12.0,  // mm - Ağız genişliği
  goz: 9.0,         // mm - Göz boyutu
  ust_dudak: 6.0,   // mm - Üst dudak
  onarka_bas: 75.0  // mm - Alias for OFD
};

/**
 * FetalHead3DViewer Component
 * 
 * @param {Object} measurements - Biyometrik ölçüler (slider değerleri)
 * @param {string} gender - Cinsiyet ('male' | 'female')
 * @param {string} className - CSS class
 * @param {function} onMeasurementChange - Ölçü değişikliği callback (FaceWarp için)
 */
export const FetalHead3DViewer = ({ measurements, gender, className, onMeasurementChange }) => {
  // === REFS ===
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const animationIdRef = useRef(null);
  const morphSystemRef = useRef(null);
  const measurementsRef = useRef(measurements);
  const isInitializedRef = useRef(false);
  
  // === FACE WARP STATE ===
  const faceWarpRef = useRef({
    active: false,
    region: null,
    startPoint: null,
    startValue: null
  });
  const highlightMeshRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  // Measurements ref'ini güncel tut
  useEffect(() => {
    measurementsRef.current = measurements;
  }, [measurements]);

  /**
   * FaceWarp Bölge Tanımları - Geliştirilmiş
   * Her bölge: 3D pozisyon, etki alanı, bağlı ölçüler, hareket yönleri
   */
  const FACE_REGIONS = {
    forehead: {
      name: 'Alın',
      icon: '🧠',
      center: new THREE.Vector3(0, 0.35, 0.4),
      radius: 0.4,
      measurements: {
        vertical: 'fromen',    // Yukarı/aşağı = alın uzunluğu
        horizontal: 'bioc_cap' // Sağa/sola = yüz genişliği
      },
      color: 0x9333ea,
      sensitivity: { vertical: 0.8, horizontal: 0.5 }
    },
    eyes: {
      name: 'Gözler',
      icon: '👁️',
      center: new THREE.Vector3(0, 0.1, 0.5),
      radius: 0.3,
      measurements: {
        vertical: 'goz',       // Yukarı/aşağı = göz derinliği
        horizontal: 'goztepe'  // Sağa/sola = göz arası mesafe
      },
      color: 0x3b82f6,
      sensitivity: { vertical: 0.4, horizontal: 0.6 }
    },
    nose: {
      name: 'Burun',
      icon: '👃',
      center: new THREE.Vector3(0, -0.08, 0.7),
      radius: 0.25,
      measurements: {
        vertical: 'burun',     // Yukarı/aşağı = burun uzunluğu (burun ucu)
        horizontal: 'bioc_cap' // Sağa/sola = basık/geniş burun
      },
      color: 0x22c55e,
      sensitivity: { vertical: 0.5, horizontal: 0.3 }
    },
    chin: {
      name: 'Çene',
      icon: '🗣️',
      center: new THREE.Vector3(0, -0.45, 0.35),
      radius: 0.35,
      measurements: {
        vertical: 'cene',      // Yukarı/aşağı = çene uzunluğu
        horizontal: 'agiz_capi' // Sağa/sola = ağız genişliği
      },
      color: 0xf97316,
      sensitivity: { vertical: 0.8, horizontal: 0.4 }
    }
  };

  /**
   * Tıklanan noktanın hangi yüz bölgesine ait olduğunu bul
   */
  const detectFaceRegion = useCallback((point) => {
    if (!point) return null;
    
    let closestRegion = null;
    let minDistance = Infinity;
    
    for (const [key, region] of Object.entries(FACE_REGIONS)) {
      const distance = point.distanceTo(region.center);
      if (distance < region.radius && distance < minDistance) {
        minDistance = distance;
        closestRegion = key;
      }
    }
    
    return closestRegion;
  }, []);

  /**
   * Highlight mesh oluştur (seçili bölgeyi göster)
   */
  const createHighlightMesh = useCallback((regionKey) => {
    const region = FACE_REGIONS[regionKey];
    if (!region) return null;
    
    const geometry = new THREE.SphereGeometry(region.radius, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: region.color,
      transparent: true,
      opacity: 0.25,
      wireframe: false,
      side: THREE.DoubleSide
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(region.center);
    
    return mesh;
  }, []);

  /**
   * FaceWarp: Mouse down handler
   */
  const handleFaceWarpStart = useCallback((event, intersectPoint) => {
    const regionKey = detectFaceRegion(intersectPoint);
    
    if (regionKey && onMeasurementChange) {
      const region = FACE_REGIONS[regionKey];
      
      // Controls'u devre dışı bırak
      if (controlsRef.current) {
        controlsRef.current.enabled = false;
      }
      
      // Highlight ekle
      if (sceneRef.current) {
        // Eski highlight'ı kaldır
        if (highlightMeshRef.current) {
          sceneRef.current.remove(highlightMeshRef.current);
        }
        
        // Yeni highlight ekle
        const highlight = createHighlightMesh(regionKey);
        if (highlight) {
          sceneRef.current.add(highlight);
          highlightMeshRef.current = highlight;
        }
      }
      
      // Başlangıç değerlerini kaydet
      const startValues = {};
      if (region.measurements.vertical) {
        startValues[region.measurements.vertical] = measurementsRef.current[region.measurements.vertical] || REFERENCE[region.measurements.vertical];
      }
      if (region.measurements.horizontal) {
        startValues[region.measurements.horizontal] = measurementsRef.current[region.measurements.horizontal] || REFERENCE[region.measurements.horizontal];
      }
      
      faceWarpRef.current = {
        active: true,
        region: regionKey,
        startPoint: { x: event.clientX, y: event.clientY },
        startValues
      };
      
      console.log(`FaceWarp başladı: ${region.name}`, startValues);
    }
  }, [detectFaceRegion, createHighlightMesh, onMeasurementChange]);

  /**
   * FaceWarp: Mouse move handler - Geliştirilmiş
   */
  const handleFaceWarpMove = useCallback((event) => {
    if (!faceWarpRef.current.active || !onMeasurementChange) return;
    
    const { region, startPoint, startValues } = faceWarpRef.current;
    const regionConfig = FACE_REGIONS[region];
    
    if (!regionConfig || !startPoint) return;
    
    // Delta hesapla
    const deltaY = (startPoint.y - event.clientY); // Yukarı = pozitif
    const deltaX = (event.clientX - startPoint.x); // Sağa = pozitif
    
    // Dikey hareket - BÖLGEYE GÖRE YÖN BELİRLE
    if (regionConfig.measurements.vertical) {
      const m = regionConfig.measurements.vertical;
      const startVal = startValues[m];
      const ref = REFERENCE[m];
      const sensitivity = regionConfig.sensitivity.vertical;
      
      // Çene için: Aşağı sürükle = ARTIŞ (negatif deltaY = pozitif değişim)
      // Diğerleri için: Yukarı sürükle = ARTIŞ
      let effectiveDelta;
      if (region === 'chin') {
        effectiveDelta = -deltaY; // Çene: aşağı = artış
      } else {
        effectiveDelta = deltaY;  // Diğerleri: yukarı = artış
      }
      
      const newVal = Math.max(ref * 0.2, Math.min(ref * 2.5, startVal + (effectiveDelta * sensitivity)));
      onMeasurementChange(m, newVal);
    }
    
    // Yatay hareket (sağa/sola) - genişlik/basıklık
    if (regionConfig.measurements.horizontal) {
      const m = regionConfig.measurements.horizontal;
      const startVal = startValues[m];
      const ref = REFERENCE[m];
      const sensitivity = regionConfig.sensitivity.horizontal;
      
      // Sağa sürükleme = ARTIŞ
      const newVal = Math.max(ref * 0.2, Math.min(ref * 2.5, startVal + (deltaX * sensitivity)));
      onMeasurementChange(m, newVal);
    }
  }, [onMeasurementChange]);

  /**
   * FaceWarp: Mouse up handler
   */
  const handleFaceWarpEnd = useCallback(() => {
    if (faceWarpRef.current.active) {
      console.log('FaceWarp bitti');
      
      // Controls'u tekrar etkinleştir
      if (controlsRef.current) {
        controlsRef.current.enabled = true;
      }
      
      // Highlight'ı kaldır
      if (highlightMeshRef.current && sceneRef.current) {
        sceneRef.current.remove(highlightMeshRef.current);
        highlightMeshRef.current = null;
      }
      
      faceWarpRef.current = {
        active: false,
        region: null,
        startPoint: null,
        startValues: null
      };
    }
  }, []);

  /**
   * Ölçü key'lerini normalize et
   * onarka_bas → ofd dönüşümü
   */
  const normalizeKeys = useCallback((raw) => {
    if (!raw) return { ...REFERENCE };
    
    const normalized = {};
    
    // Tüm değerleri kopyala
    for (const [key, value] of Object.entries(raw)) {
      // onarka_bas → ofd alias
      if (key === 'onarka_bas') {
        normalized['ofd'] = value;
      }
      normalized[key] = value;
    }
    
    // Eksik değerler için referans kullan
    for (const [key, refValue] of Object.entries(REFERENCE)) {
      if (normalized[key] === undefined || normalized[key] === null) {
        normalized[key] = refValue;
      }
    }
    
    return normalized;
  }, []);

  /**
   * Materyal ayarla (cinsiyete göre) - Gelişmiş SSS benzeri deri görünümü
   */
  const setupMaterial = useCallback((mesh, genderVal) => {
    if (!mesh) return;

    // Cinsiyete göre deri renkleri
    const skinBase = genderVal === 'female'
      ? new THREE.Color(0.98, 0.82, 0.76)  // Daha canlı pembe
      : new THREE.Color(0.92, 0.80, 0.74); // Daha sıcak ten rengi

    mesh.traverse((child) => {
      if (child.isMesh && child.material) {
        // SSS benzeri subsurface scattering efekti için özel materyal
        child.material = new THREE.MeshPhysicalMaterial({
          color: skinBase,
          roughness: 0.45,
          metalness: 0.0,
          clearcoat: 0.1,           // Hafif parlaklık
          clearcoatRoughness: 0.6,
          sheen: 0.3,               // Deri görünümü için sheen
          sheenRoughness: 0.4,
          sheenColor: genderVal === 'female' 
            ? new THREE.Color(0.95, 0.65, 0.65)  // Pembe sheen
            : new THREE.Color(0.85, 0.72, 0.68), // Nötr sheen
          transmission: 0.05,       // Hafif ışık geçirgenliği (SSS efekti)
          thickness: 0.5,
          envMapIntensity: 0.3,
          side: THREE.DoubleSide,
          flatShading: false
        });
        
        // Smooth normals için
        if (child.geometry) {
          child.geometry.computeVertexNormals();
        }
      }
    });
  }, []);

  /**
   * Ana useEffect - Scene kurulumu
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Mevcut canvas'ları temizle
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Reset
    isInitializedRef.current = false;

    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    // === SCENE ===
    const scene = new THREE.Scene();
    
    // Gradient arka plan için canvas texture
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = 512;
    bgCanvas.height = 512;
    const bgCtx = bgCanvas.getContext('2d');
    
    // Radyal gradient - daha profesyonel görünüm
    const gradient = bgCtx.createRadialGradient(256, 256, 0, 256, 256, 400);
    if (gender === 'female') {
      gradient.addColorStop(0, '#2d1f3d');   // Merkez - koyu mor
      gradient.addColorStop(0.5, '#1a1525'); // Orta
      gradient.addColorStop(1, '#0f0d14');   // Kenar - daha koyu
    } else {
      gradient.addColorStop(0, '#1f2d3d');   // Merkez - koyu mavi
      gradient.addColorStop(0.5, '#151a25'); // Orta
      gradient.addColorStop(1, '#0d0f14');   // Kenar
    }
    bgCtx.fillStyle = gradient;
    bgCtx.fillRect(0, 0, 512, 512);
    
    const bgTexture = new THREE.CanvasTexture(bgCanvas);
    scene.background = bgTexture;
    sceneRef.current = scene;

    // === CAMERA ===
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 4);
    cameraRef.current = camera;

    // === RENDERER - Yüksek kalite ===
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
      stencil: false
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    renderer.shadowMap.enabled = false; // Performance
    renderer.physicallyCorrectLights = true; // Gerçekçi ışık
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // === CONTROLS ===
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enableZoom = true;
    controls.enablePan = false;
    controls.minDistance = 1.5;
    controls.maxDistance = 8;
    controls.rotateSpeed = 0.8;
    controlsRef.current = controls;

    // === LIGHTING - Profesyonel Stüdyo Aydınlatması ===
    
    // Ambient - Ortam ışığı (gölgeleri yumuşatır)
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Hemisphere light - Doğal ortam ışığı (yukarıdan mavi, aşağıdan sıcak)
    const hemiLight = new THREE.HemisphereLight(
      0xffffff,  // Yukarı rengi
      gender === 'female' ? 0xffc0cb : 0xccddff, // Aşağı rengi
      0.3
    );
    scene.add(hemiLight);

    // Key light - Ana ışık (sağ üstten)
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
    keyLight.position.set(3, 4, 4);
    keyLight.castShadow = false;
    scene.add(keyLight);

    // Fill light - Dolgu ışığı (sol taraftan, daha yumuşak)
    const fillLight = new THREE.DirectionalLight(
      gender === 'female' ? 0xffccdd : 0xccddff,
      0.4
    );
    fillLight.position.set(-4, 2, 3);
    scene.add(fillLight);

    // Rim light - Kenar vurgusu (arkadan)
    const rimLight = new THREE.DirectionalLight(
      gender === 'female' ? 0xff88aa : 0x88aaff,
      0.6
    );
    rimLight.position.set(0, 0, -5);
    scene.add(rimLight);

    // Top light - Üstten ışık (saç/kafa vurgusu)
    const topLight = new THREE.DirectionalLight(0xffffff, 0.3);
    topLight.position.set(0, 6, 0);
    scene.add(topLight);

    // Bottom fill - Alttan hafif dolgu (çene gölgelerini azaltır)
    const bottomFill = new THREE.DirectionalLight(
      gender === 'female' ? 0xffddee : 0xddeeff,
      0.15
    );
    bottomFill.position.set(0, -3, 2);
    scene.add(bottomFill);

    // === GRID - Profesyonel medikal görünüm ===
    const gridColor = gender === 'female' ? 0x9333ea : 0x3b82f6;
    const gridSubColor = gender === 'female' ? 0x4c1d95 : 0x1e3a5f;
    
    // Ana grid
    const gridHelper = new THREE.GridHelper(5, 25, gridColor, gridSubColor);
    gridHelper.rotation.x = Math.PI / 2;
    gridHelper.position.z = -1.8;
    gridHelper.material.opacity = 0.4;
    gridHelper.material.transparent = true;
    scene.add(gridHelper);
    
    // İkinci derinlik grid'i (3D derinlik hissi için)
    const gridHelper2 = new THREE.GridHelper(5, 25, gridColor, gridSubColor);
    gridHelper2.rotation.x = Math.PI / 2;
    gridHelper2.position.z = -2.5;
    gridHelper2.material.opacity = 0.15;
    gridHelper2.material.transparent = true;
    scene.add(gridHelper2);
    
    // Zemin grid'i
    const floorGrid = new THREE.GridHelper(4, 20, gridSubColor, gridSubColor);
    floorGrid.position.y = -1.2;
    floorGrid.material.opacity = 0.2;
    floorGrid.material.transparent = true;
    scene.add(floorGrid);

    // === MORPH SYSTEM ===
    morphSystemRef.current = createFetalMorphSystem();

    // === MODEL LOADING ===
    const loader = new GLTFLoader();
    loader.load(
      '/models/fetal_head.glb',
      (gltf) => {
        console.log('Model yüklendi, morph sistemi başlatılıyor...');

        const model = gltf.scene;

        // Bounding box
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // Scale
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = maxDim > 0 ? 2.0 / maxDim : 1;
        model.scale.setScalar(scale);

        // Center
        model.position.set(
          -center.x * scale,
          -center.y * scale,
          -center.z * scale
        );

        // Material
        setupMaterial(model, gender);

        // Find mesh and init morph system
        let targetMesh = null;
        model.traverse((child) => {
          if (child.isMesh && child.geometry) {
            targetMesh = child;
          }
        });

        if (targetMesh && morphSystemRef.current) {
          const success = morphSystemRef.current.initialize(targetMesh.geometry);
          if (success) {
            isInitializedRef.current = true;
            console.log('Morph sistemi başarıyla başlatıldı');
            
            // İlk ölçüleri uygula
            const initialMeasurements = normalizeKeys(measurementsRef.current);
            const positions = targetMesh.geometry.attributes.position;
            morphSystemRef.current.updateAndApply(initialMeasurements, positions);
            targetMesh.geometry.computeVertexNormals();
          }
        }

        modelRef.current = targetMesh;
        scene.add(model);

        // Camera setup
        camera.position.set(0, 0, 3.5);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();

        renderer.render(scene, camera);
      },
      undefined,
      (error) => console.error('Model yükleme hatası:', error)
    );

    // === ANIMATION LOOP ===
    let lastHash = '';

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      // Morph uygula
      if (isInitializedRef.current && modelRef.current && morphSystemRef.current) {
        const current = measurementsRef.current;
        const hash = JSON.stringify(current);

        if (hash !== lastHash) {
          lastHash = hash;

          const normalized = normalizeKeys(current);
          const positions = modelRef.current.geometry.attributes.position;
          
          morphSystemRef.current.updateAndApply(normalized, positions);
          modelRef.current.geometry.computeVertexNormals();
        }
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // === RESIZE ===
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);

    // === FACE WARP EVENT HANDLERS ===
    
    // Raycast helper
    const getIntersectPoint = (event) => {
      if (!modelRef.current) return null;
      
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      
      const intersects = raycasterRef.current.intersectObject(modelRef.current, true);
      if (intersects.length > 0) {
        return intersects[0].point;
      }
      return null;
    };
    
    // Double-click handler - FaceWarp başlat
    const handleDoubleClick = (event) => {
      const point = getIntersectPoint(event);
      if (point) {
        handleFaceWarpStart(event, point);
      }
    };
    
    // Mouse move handler
    const handleMouseMove = (event) => {
      handleFaceWarpMove(event);
    };
    
    // Mouse up handler
    const handleMouseUp = () => {
      handleFaceWarpEnd();
    };
    
    // Event listener'ları ekle
    renderer.domElement.addEventListener('dblclick', handleDoubleClick);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // === CLEANUP ===
    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('dblclick', handleDoubleClick);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      if (morphSystemRef.current) morphSystemRef.current.dispose();
      if (controlsRef.current) controlsRef.current.dispose();
      if (rendererRef.current) rendererRef.current.dispose();
    };
  }, [gender, normalizeKeys, setupMaterial, handleFaceWarpStart, handleFaceWarpMove, handleFaceWarpEnd]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative ${className || ''}`}
      style={{ minHeight: '400px', cursor: 'crosshair' }}
    >
      {/* FaceWarp İpucu */}
      {onMeasurementChange && (
        <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded-lg pointer-events-none z-10">
          <span className="opacity-80">💡 Çift tıkla:</span>
          <span className="ml-1">Alın</span>
          <span className="mx-1">•</span>
          <span>Göz</span>
          <span className="mx-1">•</span>
          <span>Burun</span>
          <span className="mx-1">•</span>
          <span>Çene</span>
        </div>
      )}
    </div>
  );
};

export default FetalHead3DViewer;
